<template>
  <div
    class="flex w-full flex-col justify-between space-y-4 rounded-md bg-white p-6 shadow-md"
  >
    <div class="flex-row-full-justify-between">
      <data-orchestration-step-status
        v-model:orchestration-history-open="orchestrationHistoryOpen"
        :show-orch-history="showOrchHistory"
        class="min-w-0"
      />

      <div class="flex items-center space-x-4">
        <data-orchestration-master-status-dropdown
          v-if="!isOrchestrationPaused"
          :model="activeDeal"
          :log="activeDataOrchestrationLog"
        />
        <div
          v-if="allDataOrchestrationLogs.length > 0"
          v-tooltip="$t('ORCHESTRATION.VERSION_HISTORY')"
          data-cy="version-history-button"
          class="flex h-10 w-10 items-center justify-center rounded border hover:cursor-pointer hover:bg-gray-50"
          @click="setShowVersionHistoryOverlay(!showVersionHistoryOverlay)"
        >
          <icon-base
            class="-mt-1"
            :icon="IconHistory"
            :icon-color="UTIL_COLORS.LABEL"
          />
        </div>
        <access-control :exclude="[USER, CLIENT_ANALYST]">
          <run-data-orchestration-button />
        </access-control>
      </div>
    </div>
    <loader
      v-if="gettingHistoryLogs"
      is-loading
      background-color="transparent"
    />
    <data-orchestration-history-table
      v-else-if="showOrchHistory && orchestrationHistoryOpen"
    />
    <slot />
  </div>
</template>

<script setup lang="ts">
import { useToggle } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import {
  CLIENT_ANALYST,
  PAUSED_ORCHESTRATION_STATUS,
  USER,
  UTIL_COLORS,
} from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { useDealsBase } from "@/hooks/deals";
import { useUnderwritingLogs } from "@/hooks/underwriting";
import useApplicationsStore from "@/stores/applications";

import AccessControl from "@/components/AccessControl.vue";
import RunDataOrchestrationButton from "@/components/deals/dataOrchestration/run/RunDataOrchestrationButton.vue";
import DataOrchestrationHistoryTable from "@/components/deals/dataOrchestration/status/DataOrchestrationHistoryTable.vue";
import DataOrchestrationStepStatus from "@/components/deals/dataOrchestration/status/DataOrchestrationStepStatus.vue";
import IconHistory from "@/components/icons/IconHistory.vue";
import DataOrchestrationMasterStatusDropdown from "@/components/ui/DataOrchestrationMasterStatusDropdown.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";

const { activeDeal } = useDealsBase();
const { dispatch } = useStore();
const { historyLogs, gettingHistoryLogs } = useUnderwritingLogs();
const applicationsStore = useApplicationsStore();
const { authClientSettings } = useClients();
const { isClient } = useAuth();
const {
  activeDataOrchestrationLog,
  showVersionHistoryOverlay,
  allDataOrchestrationLogs,
} = storeToRefs(applicationsStore);
const setShowVersionHistoryOverlay = useToggle(showVersionHistoryOverlay);

const orchestrationHistoryOpen = ref(true);

const showOrchHistory = computed(() => !!historyLogs.value.length);

const isOrchestrationPaused = computed(
  () => activeDataOrchestrationLog.value?.status === PAUSED_ORCHESTRATION_STATUS
);

onMounted(() => {
  if (isClient && !authClientSettings.value?.can_manage_data_orchestration) {
    return;
  }
  dispatch("orchestration/getDataServices");
});
</script>
